/**
 * Binding a refused write back to the control that caused it.
 *
 * Two pure functions, shared rather than copied: f2.1 pinned ONE error
 * envelope for the whole API, so every dialog on the platform reads a refusal
 * the same way -- `errors[]` when the server could name a field, `message`
 * when it could not. They started life inside the Business Unit Groups screen
 * and moved here the moment a second screen needed them, because two copies of
 * "how a refusal reaches the user" is how two screens come to disagree about
 * whether a 409 is a field problem.
 */

import { ApiError, type ErrorItem } from '@/api/client';

/**
 * The server's problem for one field, bound back to its control by key.
 *
 * Returns null when the refusal named no field -- the dialog then shows the
 * envelope's `message` instead, so a whole-request refusal is never silently
 * swallowed.
 */
export function problemFor(error: unknown, key: string): string | null {
  if (!(error instanceof ApiError)) return null;
  const found = error.errors.find((item: ErrorItem) => item.key === key);
  return found ? found.message : null;
}

/** The refusal's general message, shown only when no field claimed it. */
export function generalMessage(error: unknown, ...fieldKeys: string[]): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.errors.some((item: ErrorItem) => fieldKeys.includes(item.key))) return null;
  return error.message;
}
